import { readItems } from '@directus/sdk';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { queryKeysFactory } from '../../lib/queryKeysFactory.ts';
import { directusClient } from '../../services/directusClient/directusClient.ts';

const uiResultSchema = z.object({
  key: z.string(),
  namespace: z.string(),
  translations: z.array(
    z.object({
      value: z.string(),
      languages_id: z.object({
        code: z.string(),
      }),
    }),
  ),
});

export const useTranslations = (locale: string | null) => {
  return useQuery({
    // staleTime: 60 * (60 * 1000), // 60 mins
    queryKey: queryKeysFactory('translations').list(locale ? [locale] : []),
    queryFn: async () => {
      if (!locale) {
        return null;
      }

      // retrieve translations under the namespace 'promoter'
      const uiPromoterTranslations = await directusClient.request(
        readItems('ui', {
          fields: [
            '*',
            {
              translations: ['value', { languages_id: ['code'] }],
            },
          ],
          filter: {
            namespace: {
              _eq: 'promoter',
            },
          },
          deep: {
            translations: {
              _filter: {
                languages_id: {
                  code: {
                    _eq: locale,
                  },
                },
              },
            },
          },
          limit: -1,
        }),
      );

      const parsedUiPromoterTranslations = uiPromoterTranslations
        .map((uiResultItem) => uiResultSchema.safeParse(uiResultItem))
        .filter((item) => item.success)
        .map((item) => item.data);

      const promoter: Record<string, string> = {};
      for (const item of parsedUiPromoterTranslations) {
        // A key without a translation in this locale is left out, so t() returns undefined and
        // the caller's fallback text shows.
        const value = item.translations[0]?.value;
        if (value !== undefined) {
          promoter[item.key] = value;
        }
      }

      return { promoter };
    },
  });
};
